<template>
	<span class="small">
		{{ title }}
		<strong :class="['time-remaining', { 'text-danger' : expiring }]">{{ remainingMinutes }}<span class="sr-only"> minutes</span>:<span class="time-remaining-seconds">{{ remainingSeconds }}<span class="sr-only"> seconds</span></span></strong>
		<span v-if="expirationAlert" class="sr-only" aria-live="polite">
			{{ timeRemainingNotification }}
		</span>
	</span>
</template>

<script>
import { defineComponent } from 'vue';
import moment from 'moment';
import { interval } from 'rxjs';
import { map } from 'rxjs/operators';
import { momentProvider } from '@/services/clock.service';

let tickerSubscription;
let expiresAt;

export default defineComponent({
	name: 'ExpireTimer',
	props: {
		title: {
			type: String,
			default: ''
		},
		expirationDate: {
			type: moment,
			required: true
		},
		expirationAlert: { // Number of seconds remaining when alert (modal) is shown. If not defined, timer is display-only (won't emit timer-alert and timer-expired).
			type: Number,
			required: false,
			default: 0
		}
	},
	data() {
		return {
			totalSecondsRemaining: 0,
			alertFired: false
		};
	},
	computed: {
		remainingMinutes() {
			return Math.floor(this.totalSecondsRemaining / 60);
		},
		remainingSeconds() {
			return String(this.totalSecondsRemaining % 60).padStart(2, 0);
		},
		expiring() {
			return this.totalSecondsRemaining <= 60;
		},
		expired() {
			return this.totalSecondsRemaining <= 0;
		},
		timeRemainingNotification() {
			// Notifications for screen readers
			if (this.totalSecondsRemaining === 0) {
				return 'The time to complete your reservation has expired.';
			} else if (this.totalSecondsRemaining <= 60) {
				return 'Less than one minute left to complete reservation.';
			} else if (this.totalSecondsRemaining <= 300) {
				return 'Less than five minutes left to complete reservation.';
			} else {
				return '';
			}
		}
	},
	watch: {
		expirationDate: function() {
			this.setNewExpiration();
		}
	},
	mounted() {
		const clockObservable$ = interval(1000).pipe(
			map(() => momentProvider())
		);

		tickerSubscription = clockObservable$.subscribe(m => {
			this.tickTimer(m, null);
		});

		this.setNewExpiration();

	},
	destroyed() {
		tickerSubscription.unsubscribe();
	},
	methods: {
		setNewExpiration() {
			if (this.expirationDate) {
				this.alertFired = false;
				this.tickTimer(momentProvider(), this.expirationDate);
			}
		},
		tickTimer(m, newExpiration) {
			if (newExpiration) {
				expiresAt = newExpiration;
			}

			if (!expiresAt) {
				return;
			}

			this.totalSecondsRemaining = Math.floor(expiresAt.diff(m) / 1000);
			if (this.totalSecondsRemaining < 0) { this.totalSecondsRemaining = 0; }

			if (this.expirationAlert) {
				if (this.totalSecondsRemaining <= this.expirationAlert && !this.alertFired) {
					this.alertFired = true;
					this.$emit('timer-alert');
				}
				if (this.expired) {
					this.$emit('timer-expired');
				}
			}
		}
	}
});
</script>


<style scoped lang="scss">
.time-remaining {
	white-space: nowrap;
}
.time-remaining-seconds {
	display: inline-block;
	text-align: left;
	width: 20px;
}
.text-danger {
	animation: pulse 1s infinite;
	display: inline-block;
}
@keyframes pulse {
	0% { transform: scale(1.25); }
	100% { transform: scale(1); }
}
</style>
